import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, input } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { Router } from '@angular/router';
import { RootReducer, Store } from '@app/app.reducers';
import {
	GetProductQuantityPipe,
	GetQuantityUnitPipe,
} from '@app/checkout/modules/checkout-shared/pipes/product.pipes';
import {
	addProduct,
	getAddedProduct,
	getItemPrices,
	getPriceForItem,
	removeItem,
	resetAddedProduct,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { isPrintfileAvailable } from '@app/checkout/modules/checkout-shared/utils/checkout-utils';
import { ProductSource } from '@app/product/product-types';
import { SelectedOptions, getProductsForOrder } from '@app/product/reducers/product.reducer';
import { ProductPreflightService } from '@app/product/services/product-preflight.service';
import {
	ProductWithId,
	camelCase,
	getProductAssetUrl,
	hasApprovalWorkflow,
	hasUserProvidedPrintfile,
	isDownloadableProduct,
} from '@app/product/utils/product-utils';
import { ProductImageComponent } from '@app/shared/components/product-image/product-image.component';
import { popErrorToast, popSuccessToast } from '@app/shared/reducers/toast.reducer';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { ProductImageService, ProofImageDetails } from '@app/shared/services/product-image.service';
import { SnackBarService } from '@app/shared/services/snackbar.service';
import { GetTextPipe } from '@app/shared/utils/i18n.service';
import { filterTruthy, firstTruthy, popGenericErrorToast } from '@app/shared/utils/util';
import { isFailure, pluckSuccessData } from '@granodigital/grano-remote-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

// TODO: This whole component should be refactored.

/** A component for displaying the order product list. */
@UntilDestroy()
@Component({
	selector: 'g-order-product-list',
	templateUrl: './order-product-list.component.html',
	styleUrls: ['./order-product-list.component.scss'],
	imports: [
		DecimalPipe,
		ProductImageComponent,
		FlexLayoutModule,
		GetProductQuantityPipe,
		GetQuantityUnitPipe,
		GetTextPipe,
	],
})
export class OrderProductListComponent implements OnInit {
	readonly order$ = input.required<api.OrderDto>({ alias: 'order' });
	readonly showCopyToBasket$ = input(true, { alias: 'showCopyToBasket' });
	readonly showPrices$ = input(true, { alias: 'showPrices' });

	readonly itemData$ = computed(() => {
		const order = this.order$();
		const items = order?.products || [];
		const productImages = new Map<string, api.ProductImageDto[]>();
		const proofImageDetails = new Map<string, ProofImageDetails | undefined>();

		for (const item of items) {
			productImages.set(item.uuid, this.productImageService.collectAllImages(item));
			proofImageDetails.set(item.uuid, this.productImageService.createProofImageDetails(item));
		}

		return { items, productImages, proofImageDetails };
	});

	private readonly store = inject(Store<RootReducer.State>);
	private readonly router = inject(Router);
	private readonly dataLayer = inject(DataLayer);
	private readonly preflight = inject(ProductPreflightService);
	private readonly newRelic = inject(NewRelic);
	private readonly productImageService = inject(ProductImageService);

	isOrderStatusReady!: boolean;
	stockLevels = new Map<number, number>();
	hasUserProvidedPrintfile = hasUserProvidedPrintfile;
	private existingProducts: ProductWithId[] = [];
	protected isOrderConfirmation!: boolean;
	private readonly window = globalThis;
	private readonly productsForOrder$ = this.store.select(getProductsForOrder);
	readonly redirectToShoppingCart = (): Promise<boolean> => this.router.navigate(['/checkout/cart']);

	readonly buttonVisibilityData$ = computed(() => {
		const itemData = this.itemData$();
		const isButtonColumnVisible = new Map<string, boolean>();
		const isDownloadButtonVisible = new Map<string, boolean>();
		const isCopyButtonVisible = new Map<string, boolean>();
		const buttonColumnLayout = new Map<string, string>();

		for (const item of itemData.items) {
			this.setButtonVisibilityInMap(
				item,
				isButtonColumnVisible,
				isDownloadButtonVisible,
				isCopyButtonVisible,
			);
			this.setButtonColumnLayoutInMap(item, isButtonColumnVisible, buttonColumnLayout);
		}

		return { isButtonColumnVisible, isDownloadButtonVisible, isCopyButtonVisible, buttonColumnLayout };
	});

	private setButtonVisibilityInMap(
		item: api.OrderProductDto,
		isButtonColumnVisible: Map<string, boolean>,
		isDownloadButtonVisible: Map<string, boolean>,
		isCopyButtonVisible: Map<string, boolean>,
	): void {
		if (this.isOrderConfirmation) {
			isCopyButtonVisible.set(item.uuid, false);
			isDownloadButtonVisible.set(item.uuid, false);
			isButtonColumnVisible.set(item.uuid, false);
			return;
		}

		if (this.showPrintfileError(item)) {
			isCopyButtonVisible.set(item.uuid, false);
			isDownloadButtonVisible.set(item.uuid, false);
			isButtonColumnVisible.set(item.uuid, false);
			return;
		}

		const isDownloadAllowed = this.isDownloadAllowed(item);
		const showCopyToBasket = this.showCopyToBasket$();

		isCopyButtonVisible.set(
			item.uuid,
			showCopyToBasket &&
				isPrintfileAvailable({
					id: item.uuid,
					options: { printfile: item.printfile },
					product: { ...item.product, id: item.product!.id! },
				}),
		);
		isDownloadButtonVisible.set(item.uuid, isDownloadAllowed);
		isButtonColumnVisible.set(item.uuid, showCopyToBasket || isDownloadAllowed);
	}

	/** Helper method for setting button column layout in computed signal Maps */
	private setButtonColumnLayoutInMap(
		item: api.OrderProductDto,
		isButtonColumnVisible: Map<string, boolean>,
		buttonColumnLayout: Map<string, string>,
	): void {
		buttonColumnLayout.set(
			item.uuid,
			isButtonColumnVisible.get(item.uuid) ? '80px 2fr 1fr 1fr 3fr' : '80px 5fr 1fr 1fr',
		);
	}

	/** Initializes the component. */
	ngOnInit(): void {
		this.productsForOrder$.pipe(filterTruthy, untilDestroyed(this)).subscribe((products) => {
			// Hide "Copy to basket" buttons until products have been fetched to prevent
			// blinking styling weirdness
			this.isOrderStatusReady = true;
			this.existingProducts = products as ProductWithId[];

			// Update stock levels for stock products (only when not on order confirmation page)
			if (!this.isOrderConfirmation) {
				this.stockLevels.clear();
				for (const product of products) {
					if (product.type === 'stock') {
						this.stockLevels.set(product.id!, product.stock_level ?? 0);
					}
				}
			}
		});
	}

	/** Checks if the product is an existing product. */
	isExistingProduct(item: Pick<api.OrderProductDto, 'product'>): boolean {
		if (this.isOrderConfirmation) return true;
		if (!item || !Number.isFinite(item?.product?.id)) return false;
		if (!this.existingProducts?.find((existing) => existing.id === item.product?.id)) return false;
		return true;
	}

	/** Downloads the asset associated with the order product. */
	downloadProductAsset(item: Pick<api.OrderProductDto, 'printfile' | 'product'>): void {
		const url = getProductAssetUrl(item);
		if (!url) {
			this.store.dispatch(
				popErrorToast({
					title: $localize`:@@DownloadAssetErrorToast:Could not find downloadable asset`,
				}),
			);
			return;
		}
		this.window.open(url, '_blank');
	}

	/** Copies the order product to the basket. */
	async copyToBasket(item: api.OrderProductDto): Promise<void> {
		this.store.dispatch(resetAddedProduct());
		const {
			product,
			options: { selected_options = {} },
			printfile,
			work_title = '',
			// TODO: Add some automated correction or validation checks here.
			// It's possible that the order data isn't always the most up to date. Let's at least fallback to an empty object.
			product_flow_data = {} as unknown as api.OrderProductProductFlowDataDto,
		} = item;

		const shouldQueuePreflight = this.preflight.shouldQueuePreflight(printfile, true, selected_options);

		// Get existing product so it has up to date data like stock levels etc
		const currentProduct = this.existingProducts?.find((existing) => existing.id === product?.id);
		// istanbul ignore if
		if (!currentProduct) throw new Error('Product not found');

		// TODO (ED-2480): Remove this when removing the old file infra. The preflight queue shouldn't be abused with editor files.
		const shouldSetFileProcessing = shouldQueuePreflight && !!product?.metadata?.profile;

		this.addToCart(
			currentProduct,
			selected_options,
			printfile,
			work_title,
			item.price!,
			product_flow_data,
			shouldSetFileProcessing,
		);

		// Trigger preflight when needed, show toast and redirect to product edit page
		const cartItem = await firstValueFrom(this.store.select(getAddedProduct).pipe(firstTruthy));
		if (shouldQueuePreflight) {
			try {
				this.store.dispatch(getPriceForItem(cartItem, { body: selected_options }));
				const price = await firstValueFrom(
					this.store.select(getItemPrices).pipe(
						map((prices) => {
							const price = prices?.get(cartItem.id);
							if (isFailure(price))
								throw new Error(price.error.body?.message ?? 'Could not fetch price');
							return price;
						}),
						filterTruthy,
						pluckSuccessData,
					),
				);

				this.preflight.queuePreflight(cartItem.id, cartItem.product, printfile, selected_options, price);
			} catch (err) {
				popGenericErrorToast(this.store);
				this.newRelic.noticeError(err);
				this.store.dispatch(removeItem(cartItem.id));
				// TODO: Use the CartItem type from the checkout reducer
				this.dataLayer.removeProductFromCart(cartItem as api.CartItemWithProductDto);
				throw err;
			}
		}

		this.store.dispatch(
			popSuccessToast({
				title: $localize`:@@ProductCopiedToCartToast:Product copied to shopping cart`,
				action: $localize`:@@GoToCartToastButton:Go to shopping cart`,
				onAction: /* istanbul ignore next -- difficult to test and trivial */ (
					service: SnackBarService,
				) => {
					service.clear();
					void this.router.navigate(['/checkout/cart']);
				},
			}),
		);
	}

	/**
	 * Checks if the printfile error should be shown for the given product.
	 * @returns True if the printfile error should be shown, false otherwise.
	 */
	showPrintfileError(item: api.OrderProductDto): boolean {
		// don't show printfile error on order confirmation page
		if (this.isOrderConfirmation) return false;
		// checks if the product is a stock or POD product
		// stocks and PODs don't have printfiles that expire so NEVER ERROR
		if (item.product && !hasUserProvidedPrintfile(item.product)) return false;
		// if printfile has expired, show error
		if (this.hasExpiredPrintfile(item)) return true;
		return false;
	}

	/** Checks if the expiration date is valid. */
	private hasExpiredPrintfile(item: api.OrderProductDto): boolean {
		if (!item?.printfile?.key) return true; // If the product has no printfile, it is considered expired.
		const expiresAt = item?.printfile?.expires_at;
		// If the product has no printfile expiration or,
		// If the product has a printfile and it has expired, return true.
		return !expiresAt || new Date(expiresAt).valueOf() < Date.now();
	}

	/** Checks if the download is allowed for the given order product. */
	private isDownloadAllowed(item: api.OrderProductDto): boolean {
		return (
			!!item?.product &&
			isDownloadableProduct(item?.product?.metadata?.order_integration ?? []) &&
			(!hasApprovalWorkflow(item.product) || item?.approval_status === 'Approved')
		);
	}

	/** Adds the product to the cart. */
	private addToCart(
		product: api.CartItemProductDto,
		selectedOptions: SelectedOptions,
		printfile: api.OrderProductPrintfileDto | undefined,
		workTitle: string,
		price: api.ProductPriceDto,
		orderProductProductFlowData: api.OrderProductProductFlowDataDto,
		isProcessing: boolean,
	) {
		const id = crypto.randomUUID();

		const cartItemProductFlowData = Object.fromEntries(
			Object.entries(orderProductProductFlowData ?? {}).map(([key, value]) => [camelCase(key), value]),
		) as api.CartItemProductFlowDataDto;

		this.store.dispatch(
			addProduct(
				id,
				product,
				{ workTitle, selectedOptions, printfile },
				{
					...cartItemProductFlowData,
					productSource: ProductSource.PreviousOrder,
				},
				isProcessing,
			),
		);
		this.dataLayer.addProductToCart({ id, product, price, options: {} });
	}
}
